import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { FaBookmark } from "react-icons/fa";
import { toast } from "../lib/toast";

const BookmarkSection = ({ currentView, currentCollection }) => {
  const [bookmarkOpen, setBookmarkOpen] = useState(false);
  const [collections, setCollections] = useState([]);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [showNewInput, setShowNewInput] = useState(false);
  const navigate = useNavigate();

  const fetchCollections = async () => {
    try {
      const res = await axiosInstance.get("/api/bookmarks/collections/");
      setCollections(res.data);
    } catch (err) {
      console.error("Failed to load bookmarks", err);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, [bookmarkOpen]);

  useEffect(() => {
    const handleBookmarkChanged = () => fetchCollections();
    window.addEventListener("bookmark-changed", handleBookmarkChanged);
    return () => window.removeEventListener("bookmark-changed", handleBookmarkChanged);
  }, []);

  const handleCreateCollection = async (e) => {
    e.preventDefault();
    const name = newCollectionName.trim();
    if (!name) return;
    try {
      await axiosInstance.post("/api/bookmarks/collections/", { name });
      setNewCollectionName("");
      setShowNewInput(false);
      toast.success("Bookmark created.");
      fetchCollections();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("Bookmark already exists.");
      }
    }
  };

  const handleCollectionClick = (collection) => {
    navigate(
      `/recipes?view=bookmarks&collection=${collection.id}&collection_name=${encodeURIComponent(collection.name)}`
    );
  };

  return (
    <div className="mb-4">
      <div className="group flex items-center justify-between gap-1">
        <button
          onClick={() => {
            setBookmarkOpen(!bookmarkOpen);
            navigate("/recipes?view=bookmarks");
          }}
          className={`inline-flex min-w-0 flex-1 items-center rounded-lg px-2 py-1.5 text-sm transition ${
            currentView === "bookmarks"
              ? "bg-blue-50 font-semibold text-blue-600 dark:bg-blue-900/30"
              : "text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700"
          }`}
        >
          <FaBookmark size={14} className="inline mr-1.5 -mt-0.5" />
          <span className="truncate">
            Bookmarked {bookmarkOpen ? "\u25BE" : "\u25B8"}
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowNewInput(!showNewInput);
              if (!bookmarkOpen) setBookmarkOpen(true);
            }}
            className="rounded px-1 text-xs text-gray-400 transition hover:text-blue-600 dark:hover:text-blue-400"
            title="New bookmark"
            aria-label="New bookmark"
          >
            +
          </button>
        </div>
      </div>

      {bookmarkOpen && (
        <div className="ml-2 mt-2 space-y-1">
          {showNewInput && (
            <form onSubmit={handleCreateCollection} className="mb-2">
              <input
                type="text"
                placeholder="Bookmark name..."
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                autoFocus
              />
            </form>
          )}

          <button
            onClick={() => navigate("/recipes?view=bookmarks")}
            className={`flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-sm transition ${
              currentView === "bookmarks" && !currentCollection
                ? "bg-blue-50 font-semibold text-blue-600 dark:bg-blue-900/30"
                : "text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
            }`}
          >
            <span>All</span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500 dark:bg-gray-700 dark:text-gray-300">
              {collections.reduce((sum, col) => sum + col.recipe_count, 0)}
            </span>
          </button>
          {collections.map((col) => (
            <div key={col.id} className="flex items-center">
              <button
                onClick={() => handleCollectionClick(col)}
                title={`${col.name} (${col.recipe_count})`}
                className={`flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-sm transition ${
                  currentCollection === String(col.id)
                    ? "bg-blue-50 font-semibold text-blue-600 dark:bg-blue-900/30"
                    : "text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
                }`}
              >
                <span className="block truncate">{col.name}</span>
                <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500 dark:bg-gray-700 dark:text-gray-300">
                  {col.recipe_count}
                </span>
              </button>
            </div>
          ))}

          {collections.length === 0 && (
            <p className="mt-2 px-2 text-xs leading-5 text-gray-400">
              No bookmarks yet. Create one here, then save recipes into it from Discover.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default BookmarkSection;
